// 뷰어 화면 (기획서 4.3, 9.2, 9.3)
import { getBook, getBookFileBlob, updateBook } from '../db/books.js';
import { getProgress, saveProgress } from '../db/progress.js';
import { decodeTextBuffer } from '../core/encoding.js';
import { TxtReader } from '../reader/txt-reader.js';
import { EpubReader } from '../reader/epub-reader.js';
import { debounce } from '../utils/debounce.js';
import { isMobileViewport } from '../utils/responsive.js';
import { toast } from '../utils/toast.js';

const THEME_COLORS = {
  light: { bg: '#FFFFFF', fg: '#1A1A1A' },
  sepia: { bg: '#FAF7F0', fg: '#3C3434' },
  gray: { bg: '#D6D2C8', fg: '#2A2A2A' },
  dark: { bg: '#1E1E1E', fg: '#D8D4CC' },
};

export class ViewerView {
  constructor(root, { bookId, settings, onBack, onSettingsChange }) {
    this.root = root;
    this.bookId = bookId;
    this.settings = settings;
    this.onBack = onBack;
    this.onSettingsChange = onSettingsChange;

    this.book = null;
    this.reader = null;
    this.isTxt = false;

    // 마지막 미저장 위치 (저장 실패 시 메모리 보관 — 기획서 9.3, 12.3)
    this._pending = null;
    this._uiHideTimer = null;
    this._destroyed = false;

    // 위치 자동 저장 디바운스 500ms (기획서 9.3)
    this._saveDebounced = debounce(() => this._flushSave(), 500);
    // resize 디바운스 300ms (기획서 8.3.5)
    this._resizeDebounced = debounce(() => this._onResize(), 300);
  }

  async mount() {
    this.root.innerHTML = this._shellHtml();
    this._cacheEls();
    this._applyThemeAttrs();
    this._bindUI();

    window.addEventListener('resize', this._resizeDebounced);

    try {
      await this._loadBook();
    } catch (err) {
      console.error(err);
      // 책 열기 실패 → 빈 뷰어 띄우지 않고 라이브러리 복귀 (기획서 12.3.1)
      toast('이 책의 파일을 찾을 수 없습니다. 다시 추가해 주세요.');
      this.onBack();
    }
  }

  _shellHtml() {
    return `
      <div class="viewer" data-ui="hidden">
        <div class="content-wrap">
          <div class="content-inner" data-width="${this.settings.contentWidth}">
            <div class="reader-mount"></div>
          </div>
        </div>

        <div class="tap-zones">
          <div data-zone="prev"></div>
          <div data-zone="ui"></div>
          <div data-zone="next"></div>
        </div>

        <header class="toolbar">
          <button data-act="back" title="서재로">‹</button>
          <div class="tb-title"></div>
          <button data-act="settings" title="글꼴/테마">Aa</button>
        </header>

        <div class="progress-bar">
          <div class="progress-row">
            <span class="pr-left"></span>
            <span class="pr-right"></span>
          </div>
          <input class="progress-slider" type="range" min="0" max="1000" value="0" />
        </div>

        <div class="panel-backdrop"></div>
        <aside class="panel" id="settingsPanel">
          <button class="panel-close" data-act="close-panel">×</button>
          <h2>읽기 설정</h2>
          ${this._settingsPanelHtml()}
        </aside>

        <div class="loading-overlay">
          <div style="text-align:center">
            <div class="spinner"></div>
            <div>책을 여는 중…</div>
          </div>
        </div>
      </div>`;
  }

  _settingsPanelHtml() {
    const s = this.settings;
    return `
      <div class="panel-section">
        <label>테마</label>
        <div class="seg" data-group="theme">
          <button data-val="light">라이트</button>
          <button data-val="sepia">세피아</button>
          <button data-val="gray">회색</button>
          <button data-val="dark">다크</button>
        </div>
      </div>
      <div class="panel-section">
        <label>글꼴</label>
        <div class="seg" data-group="fontFamily">
          <button data-val="serif">세리프</button>
          <button data-val="sans">산세리프</button>
          <button data-val="gothic">고딕</button>
          <button data-val="mono">모노</button>
        </div>
      </div>
      <div class="panel-section">
        <label>글꼴 크기 <span id="fsVal">${s.fontSize}px</span></label>
        <div class="stepper">
          <button data-act="fs-dec">−</button>
          <div class="val" id="fsBar">${s.fontSize}px</div>
          <button data-act="fs-inc">＋</button>
        </div>
      </div>
      <div class="panel-section">
        <label>줄간격</label>
        <div class="seg" data-group="lineHeight">
          <button data-val="1.5">좁게</button>
          <button data-val="1.85">보통</button>
          <button data-val="2.1">넓게</button>
        </div>
      </div>
      <div class="panel-section">
        <label>본문 너비 (데스크톱 단페이지)</label>
        <div class="seg" data-group="contentWidth">
          <button data-val="narrow">좁게</button>
          <button data-val="normal">보통</button>
          <button data-val="wide">넓게</button>
        </div>
      </div>`;
  }

  _cacheEls() {
    const $ = (sel) => this.root.querySelector(sel);
    this.viewerEl = $('.viewer');
    this.mountEl = $('.reader-mount');
    this.innerEl = $('.content-inner');
    this.toolbarTitle = $('.tb-title');
    this.slider = $('.progress-slider');
    this.prLeft = $('.pr-left');
    this.prRight = $('.pr-right');
    this.panel = $('#settingsPanel');
    this.backdrop = $('.panel-backdrop');
    this.overlay = $('.loading-overlay');
  }

  _applyThemeAttrs() {
    document.documentElement.setAttribute('data-theme', this.settings.theme);
    document.documentElement.setAttribute('data-font', this.settings.fontFamily);
    this.innerEl.setAttribute('data-width', this.settings.contentWidth);
  }

  // ───────────────────────── 책 로드 + 위치 복원 (기획서 9.2) ─────────────────────────

  async _loadBook() {
    this.book = await getBook(this.bookId);
    if (!this.book) throw new Error('book meta missing');

    const blob = await getBookFileBlob(this.bookId);
    if (!blob) throw new Error('book file missing'); // 기획서 12.3.1

    const progress = (await getProgress(this.bookId)) || {
      position: { cfi: null, globalOffset: 0, percent: 0 },
    };

    this.toolbarTitle.textContent = this.book.title;
    this.isTxt = this.book.format === 'txt';

    if (this.isTxt) {
      await this._loadTxt(blob, progress);
    } else {
      await this._loadEpub(blob, progress);
    }

    // status/lastReadAt 갱신 (기획서 9.2)
    this.book.status = this.book.status === 'finished' ? 'finished' : 'reading';
    this.book.lastReadAt = Date.now();
    try {
      await updateBook(this.book);
    } catch {
      /* 비치명적 */
    }

    this.overlay.classList.add('hidden');
    // 진입 시 잠깐 UI 노출 후 자동 숨김
    this._showUI();
  }

  async _loadTxt(blob, progress) {
    const buf = await blob.arrayBuffer();
    const { text } = decodeTextBuffer(buf); // NFC 정규화 (기획서 8.4.3)

    const content = document.createElement('div');
    content.className = 'txt-content';
    this.mountEl.appendChild(content);

    this.reader = new TxtReader(content);
    this.reader.setText(text);

    // 폰트 로딩 완료 후 측정 (기획서 12.1: document.fonts.ready).
    // ★수정: 스타일 적용과 측정을 measure() 한 곳으로 단일화.
    //   (이전엔 _applyTxtStyles 와 measure 가 폭을 따로 설정 → 불일치 버그)
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch {
        /* noop */
      }
    }
    // ★빈 화면 방어: 레이아웃이 잡힐 때까지 재시도하며 측정
    await this._measureWhenReady();

    // 위치 복원: globalOffset 우선 → 실패 시 percent fallback (기획서 0.4, 0.7, 9.2)
    const pos = progress.position || {};
    const restoreOffset =
      typeof pos.globalOffset === 'number' && pos.globalOffset >= 0
        ? pos.globalOffset
        : Math.round((pos.percent || 0) * this.reader.totalChars);

    // 모든 이동은 단일 진입점 goToOffset 경유 (기획서 0.6, 8.2.4)
    await this.reader.goToOffset(restoreOffset);

    // 복원 후 저장 percent 와 실제 차이가 크면 경고 (기획서 12.2)
    const cur = this.reader.getCurrentGlobalOffset();
    if (
      typeof pos.percent === 'number' &&
      Math.abs(cur.percent - pos.percent) > 0.05
    ) {
      console.warn('[restore] percent 편차 > 5%, 다음 저장 때 보정됨');
    }

    this.reader.onPositionChange((globalOffset, percent) =>
      this._onPositionChanged({ globalOffset, percent })
    );
    this._updateProgressUI(cur.percent);
  }

  // TXT 본문 폰트 옵션 (폭/높이는 measure() 가 컨테이너 실측으로 단일 결정)
  _txtStyleOpts() {
    const s = this.settings;
    const fontSize = isMobileViewport() ? Math.max(12, s.fontSize - 1) : s.fontSize;
    return { fontSize, lineHeight: s.lineHeight };
  }

  /**
   * measure() 를 레이아웃이 잡힐 때까지 안전하게 호출.
   * ★빈 화면 버그의 근본 방어: 측정 시점에 컨테이너 높이가 0~1px 이면
   * measure() 가 ok:false 를 반환한다. 그때는 다음 프레임에 재시도하고,
   * 최대 횟수 초과 시에도 일단 강제로 한 번 적용해 영구 공백을 막는다.
   */
  async _measureWhenReady(maxTries = 30) {
    for (let i = 0; i < maxTries; i++) {
      const r = this.reader.measure(this._txtStyleOpts());
      if (r && r.ok) return true;
      await new Promise((res) => requestAnimationFrame(() => res()));
      if (this._destroyed) return false;
    }
    // 마지막 수단: 측정이 끝내 비정상이어도 한 번은 적용 시도
    this.reader._forceMeasure?.(this._txtStyleOpts());
    return false;
  }

  async _loadEpub(blob, progress) {
    const content = document.createElement('div');
    content.className = 'epub-content';
    this.mountEl.appendChild(content);

    this.reader = new EpubReader(content);
    const pos = progress.position || {};
    await this.reader.open(blob, pos.cfi);

    // 테마 주입 (기획서 8.5.2 최소)
    const c = THEME_COLORS[this.settings.theme] || THEME_COLORS.sepia;
    this.reader.applyTheme(c);

    // CFI 복원 실패 대비: percent fallback (기획서 0.4)
    if (!pos.cfi && pos.percent > 0) {
      await this.reader.goToPercent(pos.percent);
    }

    this.reader.onPositionChange((cfi, percent) =>
      this._onPositionChanged({ cfi, percent })
    );
    this.reader.onKey((e) => this._handleKey(e));
    this._updateProgressUI(pos.percent || 0);
  }

  // ───────────────────────── 위치 저장 (기획서 9.3) ─────────────────────────

  _onPositionChanged({ globalOffset = null, cfi = null, percent }) {
    this._pending = { globalOffset, cfi, percent };
    this._updateProgressUI(percent);
    this._saveDebounced();
  }

  async _flushSave() {
    if (!this._pending || this._destroyed) return;
    const { globalOffset, cfi, percent } = this._pending;
    const ok = await saveProgress(this.bookId, { globalOffset, cfi, percent });
    if (ok) {
      this._pending = null;
      // percent ≈ 1.0 → 완독 (기획서 9.3)
      if (percent >= 0.995 && this.book && this.book.status !== 'finished') {
        this.book.status = 'finished';
        updateBook(this.book).catch(() => {});
      }
    }
    // 실패 시 _pending 유지 → 다음 디바운스 주기 재시도 (기획서 9.3, 12.3)
  }

  _updateProgressUI(percent) {
    const pct = Math.round((percent || 0) * 100);
    // MVP-1: 페이지 번호 대신 percent 표시 (기획서 10.1)
    this.prRight.textContent = `${pct}%`;
    this.prLeft.textContent = this.book ? this.book.title : '';
    if (this.slider && !this._sliderActive) {
      this.slider.value = String(Math.round((percent || 0) * 1000));
    }
  }

  // ───────────────────────── 입력 처리 (기획서 4.3) ─────────────────────────

  _bindUI() {
    // 툴바 버튼
    this.root.querySelector('[data-act="back"]').addEventListener('click', () =>
      this._goBack()
    );
    this.root.querySelector('[data-act="settings"]').addEventListener('click', () =>
      this._togglePanel(true)
    );
    this.root
      .querySelector('[data-act="close-panel"]')
      .addEventListener('click', () => this._togglePanel(false));
    this.backdrop.addEventListener('click', () => this._togglePanel(false));

    // 탭 존 (★수정: 좌우 40% 넘김 / 가운데 20% 메뉴 — 기획서 4.3 변형)
    const zones = this.root.querySelector('.tap-zones');
    zones.addEventListener('click', (e) => {
      const zone = e.target.closest('[data-zone]')?.dataset.zone;
      if (zone === 'prev') {
        this._suppressUI(); // 페이지 넘김 시 메뉴 팝업 뜨지 않도록 보장
        this._prev();
      } else if (zone === 'next') {
        this._suppressUI();
        this._next();
      } else if (zone === 'ui') {
        this._toggleUI();
      }
    });

    // 스와이프 (거리 50px 이상 — 기획서 4.3)
    let sx = 0;
    let sy = 0;
    zones.addEventListener(
      'touchstart',
      (e) => {
        sx = e.changedTouches[0].clientX;
        sy = e.changedTouches[0].clientY;
      },
      { passive: true }
    );
    zones.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - sx;
        const dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this._next();
          else this._prev();
        }
      },
      { passive: true }
    );

    // 데스크톱 마우스 움직임 → UI 표시 후 3초 무동작 시 숨김 (기획서 4.3)
    if (!isMobileViewport()) {
      this.viewerEl.addEventListener('mousemove', () => this._showUI(3000));
    }

    // 키보드 (기획서 4.3)
    this._keyHandler = (e) => this._handleKey(e);
    window.addEventListener('keydown', this._keyHandler);

    // 슬라이더 (드래그 종료 시 goToOffset — 기획서 4.3)
    this.slider.addEventListener('input', () => {
      this._sliderActive = true;
    });
    this.slider.addEventListener('change', async () => {
      this._sliderActive = false;
      const percent = Number(this.slider.value) / 1000;
      await this._seekToPercent(percent);
    });

    // 설정 패널 컨트롤
    this._bindSettingsPanel();
  }

  _handleKey(e) {
    // 입력 필드 포커스 중에는 무시 (기획서 12장)
    const tag = (e.target && e.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    switch (e.key) {
      case 'ArrowLeft':
      case 'PageUp':
        this._prev();
        break;
      case 'ArrowRight':
      case 'PageDown':
        this._next();
        break;
      case ' ':
        e.preventDefault();
        if (e.shiftKey) this._prev();
        else this._next();
        break;
      case 'Escape':
        if (this.panel.classList.contains('open')) this._togglePanel(false);
        else this._toggleUI();
        break;
      case 'f':
      case 'F':
        this._toggleFullscreen();
        break;
      default:
        break;
    }
  }

  async _next() {
    if (this.reader) await this.reader.nextPage();
  }
  async _prev() {
    if (this.reader) await this.reader.prevPage();
  }

  async _seekToPercent(percent) {
    if (!this.reader) return;
    if (this.isTxt) {
      const target = Math.round(percent * this.reader.totalChars);
      await this.reader.goToOffset(target); // 단일 진입점 (기획서 0.6)
    } else {
      await this.reader.goToPercent(percent);
    }
  }

  // ───────────────────────── UI 표시/숨김 (기획서 4.3) ─────────────────────────

  // 페이지 넘김(좌우 탭) 시 호출. 메뉴/툴바가 따라 뜨는 것을 원천 차단한다.
  // UI 가 떠 있었으면 즉시 숨기고, 자동 표시 타이머도 취소한다.
  _suppressUI() {
    if (this._uiHideTimer) {
      clearTimeout(this._uiHideTimer);
      this._uiHideTimer = null;
    }
    if (!this.panel.classList.contains('open')) {
      this.viewerEl.setAttribute('data-ui', 'hidden');
    }
  }

  _showUI(hideAfter) {
    this.viewerEl.setAttribute('data-ui', 'visible');
    if (this._uiHideTimer) clearTimeout(this._uiHideTimer);
    const ms = hideAfter || (isMobileViewport() ? 5000 : 3000);
    this._uiHideTimer = setTimeout(() => {
      if (!this.panel.classList.contains('open')) {
        this.viewerEl.setAttribute('data-ui', 'hidden');
      }
    }, ms);
  }

  _toggleUI() {
    const visible = this.viewerEl.getAttribute('data-ui') === 'visible';
    if (visible) {
      this.viewerEl.setAttribute('data-ui', 'hidden');
      if (this._uiHideTimer) clearTimeout(this._uiHideTimer);
    } else {
      this._showUI();
    }
  }

  _togglePanel(open) {
    this.panel.classList.toggle('open', open);
    this.backdrop.classList.toggle('open', open);
    if (open) this._showUI(999999);
  }

  async _toggleFullscreen() {
    try {
      if (!document.fullscreenElement) {
        await this.viewerEl.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
      if (!this.isTxt && this.reader) {
        setTimeout(() => this.reader.resize(), 100);
      }
    } catch {
      /* 일부 브라우저 미지원 — 무시 */
    }
  }

  // ───────────────────────── 설정 변경 ─────────────────────────

  _bindSettingsPanel() {
    const setActive = (group, val) => {
      this.panel
        .querySelectorAll(`[data-group="${group}"] button`)
        .forEach((b) =>
          b.classList.toggle('active', b.dataset.val === String(val))
        );
    };
    setActive('theme', this.settings.theme);
    setActive('fontFamily', this.settings.fontFamily);
    setActive('lineHeight', this.settings.lineHeight);
    setActive('contentWidth', this.settings.contentWidth);

    this.panel.querySelectorAll('.seg[data-group]').forEach((seg) => {
      const group = seg.dataset.group;
      seg.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-val]');
        if (!btn) return;
        let val = btn.dataset.val;
        if (group === 'lineHeight') val = Number(val);
        this.settings[group] = val;
        setActive(group, val);
        this._applySettingsChange(group);
      });
    });

    const fsBar = this.panel.querySelector('#fsBar');
    const fsValLabel = this.panel.querySelector('#fsVal');
    const updFs = () => {
      fsBar.textContent = this.settings.fontSize + 'px';
      fsValLabel.textContent = this.settings.fontSize + 'px';
    };
    this.panel.querySelector('[data-act="fs-dec"]').addEventListener('click', () => {
      this.settings.fontSize = Math.max(12, this.settings.fontSize - 1); // 기획서 6.3
      updFs();
      this._applySettingsChange('fontSize');
    });
    this.panel.querySelector('[data-act="fs-inc"]').addEventListener('click', () => {
      this.settings.fontSize = Math.min(24, this.settings.fontSize + 1);
      updFs();
      this._applySettingsChange('fontSize');
    });
  }

  async _applySettingsChange(group) {
    this._applyThemeAttrs();
    this.onSettingsChange(this.settings); // 영속화 (main.js)

    if (this.isTxt && this.reader) {
      // 위치는 globalOffset 이 진실이므로 보존 (기획서 0.6, 8.3.4).
      // 폰트/폭 변경 → 재측정 후 같은 offset 으로 재배치.
      const here = this.reader.getCurrentGlobalOffset().globalOffset;
      const content = this.mountEl.querySelector('.txt-content');
      if (content) {
        await this._measureWhenReady();
        // 윈도우 강제 재구성 위해 windowFrom 무효화 후 goToOffset
        this.reader.windowFrom = -1;
        this.reader.windowTo = -1;
        await this.reader.goToOffset(here);
      }
    } else if (this.reader && group === 'theme') {
      const c = THEME_COLORS[this.settings.theme] || THEME_COLORS.sepia;
      this.reader.applyTheme(c);
    }
  }

  async _onResize() {
    if (this._destroyed) return;
    if (this.isTxt && this.reader) {
      const here = this.reader.getCurrentGlobalOffset().globalOffset;
      const content = this.mountEl.querySelector('.txt-content');
      if (content) {
        await this._measureWhenReady();
        this.reader.windowFrom = -1;
        this.reader.windowTo = -1;
        await this.reader.goToOffset(here); // 기획서 8.3.5
      }
    } else if (this.reader) {
      this.reader.resize();
    }
  }

  async _goBack() {
    // 책 닫기 전 미저장 위치 즉시 flush (기획서 9.3)
    await this._flushSave();
    this.onBack();
  }

  unmount() {
    this._destroyed = true;
    this._saveDebounced.cancel();
    this._resizeDebounced.cancel();
    window.removeEventListener('resize', this._resizeDebounced);
    window.removeEventListener('keydown', this._keyHandler);
    if (this._uiHideTimer) clearTimeout(this._uiHideTimer);
    document.documentElement.removeAttribute('data-theme');
    document.documentElement.removeAttribute('data-font');
    if (this.reader) this.reader.destroy();
  }
}
